import { Music, Users, Eye, Download, Bell, Calendar, Play, DollarSign } from 'lucide-react';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export function AnalyticsPanel({ analytics }) {
  if (!analytics) return null;
  return (
            <div className="space-y-6" data-testid="analytics-content">
              <h2 className="font-cabinet text-2xl font-black uppercase tracking-tighter">
                ANALYTICS
              </h2>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="stat-card">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center">
                      <Music className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="stat-value">{analytics.total_tracks}</p>
                      <p className="stat-label">Tracks</p>
                    </div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center">
                      <Play className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="stat-value">{analytics.total_plays}</p>
                      <p className="stat-label">Plays</p>
                    </div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center">
                      <Users className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="stat-value">{analytics.total_subscribers}</p>
                      <p className="stat-label">Subscribers</p>
                    </div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#22C55E] flex items-center justify-center">
                      <DollarSign className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="stat-value">${(analytics.total_revenue || 0).toFixed(2)}</p>
                      <p className="stat-label">Revenue</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Second Stats Row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="stat-card">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center">
                      <Bell className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="stat-value">{analytics.total_presaves || 0}</p>
                      <p className="stat-label">Pre-saves</p>
                    </div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center">
                      <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="stat-value">{analytics.upcoming_tracks || 0}</p>
                      <p className="stat-label">Upcoming</p>
                    </div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center">
                      <Eye className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="stat-value">{analytics.total_views || 0}</p>
                      <p className="stat-label">Views</p>
                    </div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center">
                      <Download className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="stat-value">{analytics.total_downloads || 0}</p>
                      <p className="stat-label">Downloads</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="bg-[#141414] border border-[#262626] p-6">
                <h3 className="font-cabinet text-lg font-bold uppercase mb-4">Activity (7 Days)</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analytics.recent_analytics}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                      <XAxis dataKey="date" stroke="#A3A3A3" fontSize={12} />
                      <YAxis stroke="#A3A3A3" fontSize={12} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#141414',
                          border: '1px solid #262626',
                          borderRadius: 0
                        }}
                      />
                      <Line type="monotone" dataKey="plays" stroke="#FF3B30" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="views" stroke="#FFFFFF" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex gap-6 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-[#FF3B30]" />
                    <span className="text-sm text-[#A3A3A3]">Plays</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-white" />
                    <span className="text-sm text-[#A3A3A3]">Views</span>
                  </div>
                </div>
              </div>

              {/* Top Tracks */}
              <div className="bg-[#141414] border border-[#262626] p-6">
                <h3 className="font-cabinet text-lg font-bold uppercase mb-4">Top Tracks</h3>
                {analytics.top_tracks?.length > 0 ? (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Track</th>
                        <th>Plays</th>
                        <th>Downloads</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.top_tracks.map(track => (
                        <tr key={track.id}>
                          <td>{track.title}</td>
                          <td className="font-mono">{track.plays}</td>
                          <td className="font-mono">{track.downloads}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-[#A3A3A3]">No tracks yet</p>
                )}
              </div>
            </div>

  );
}
